import React, { useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { PHLEBOTOMY_INTAKE_URL } from "../../utils/urlConstants";

interface LegacyPhlebotomyReminderParams {
  legacyAccountId?: string;
}

/**
 * Printed on the primary clinic QR alias. Eight-character alphanumeric public
 * IDs continue as clinic scope for backend validation. Legacy LIMS values are
 * discarded without a lookup and fall through to universal intake.
 */
const LegacyPhlebotomyReminderRedirectPage = (): React.ReactElement => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { legacyAccountId } = useParams<LegacyPhlebotomyReminderParams>();

  useEffect(() => {
    const redirectToReminderIntake = () => {
      const query = new URLSearchParams(location.search);
      // This route is printed on clinic QR materials and must always open
      // reminder intake. Enrollment identifiers would incorrectly skip it.
      query.delete("tracking_id");
      query.delete("user_id");
      query.delete("lims_id");
      query.delete("clinic");

      if (legacyAccountId && /^[A-Za-z0-9]{8}$/.test(legacyAccountId)) {
        query.set("clinic", legacyAccountId);
      }

      history.replace({
        pathname: PHLEBOTOMY_INTAKE_URL,
        search: query.toString() ? `?${query.toString()}` : "",
      });
    };

    redirectToReminderIntake();
  }, [history, legacyAccountId, location.search]);

  return (
    <Box
      role="status"
      aria-label={t("phlebotomySearch.status.loading")}
      sx={{
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      <CircularProgress size={32} />
    </Box>
  );
};

export default LegacyPhlebotomyReminderRedirectPage;
